import type { AnalyticsPlatform, DateRange, DateRangePreset } from "@/types/analytics";
import { PLATFORM_OPTIONS } from "@/lib/analytics/platformHonour";
// The Analytics child list and the shared filter params are ANL-E04-S03's (plain data, no view
// code); this codec builds on them rather than keeping a second copy.
import {
  ANALYTICS_PATH,
  SHARED_FILTER_PARAMS,
  analyticsChildForPath,
  keepSharedFilterParams,
} from "@/pages/analytics/analyticsRoutes";

/**
 * The analytics filter codec (CMP-11.16, final-admin §2.3 URL contract): pure functions between the
 * page address and the filters every analytics view reads. `useUrlFilters` binds it to the router.
 *
 * - Shared params (`range`, `start`, `end`, `compare`, `granularity`, `platform`) ride along from one
 *   analytics view to another; every other param belongs to the view (a page param) and is kept as
 *   is on that view only.
 * - A default value is never written to the address, so a plain link is the default view.
 * - An unknown or invalid value falls back to its default and is reported as a reset, never thrown.
 * - There is no all-time range (AP-18): every range is bounded, and `start`/`end` are UTC calendar
 *   days with `end` inclusive.
 */

export type RangePreset = DateRangePreset;
export type CompareMode = "none" | "previous";
export type Granularity = "hour" | "day" | "week";

export type AnalyticsUrlFilters = {
  range: RangePreset;
  /** ISO date (UTC), set only when `range` is `custom`. */
  start: string | null;
  /** ISO date (UTC, inclusive), set only when `range` is `custom`. */
  end: string | null;
  compare: CompareMode;
  granularity: Granularity;
  platform: AnalyticsPlatform;
};

export type AnalyticsViewId =
  | "SCR-1"
  | "SCR-2"
  | "SCR-3"
  | "SCR-4"
  | "SCR-5"
  | "SCR-6"
  | "SCR-7"
  | "SCR-8"
  | "product-records";

export type AnalyticsViewDefinition = {
  id: AnalyticsViewId;
  /** The ranges this view offers, in display order. */
  presets: readonly RangePreset[];
  /** `hour` only where the screen declares it (UXS-06). */
  granularities: readonly Granularity[];
  /** The view's raw-window cap in days (ANL-E04-S21); null until it is measured. */
  rawWindowCapDays: number | null;
  /**
   * Shared filters this view does not read, each with the visible reason the filter bar prints
   * beside the disabled control (ANL-E04-S10). Absent where the view reads them.
   */
  ignores?: { compare?: string; granularity?: string };
};

/** One param the codec could not use: what it said, and what is used instead. */
export type FilterReset = { param: string; value: string | null; fallback: string };

export type ParsedUrlFilters = {
  filters: AnalyticsUrlFilters;
  /** The view's own params, in address order. */
  pageParams: Array<[string, string]>;
  resets: FilterReset[];
};

export type RangePresetOption = {
  key: RangePreset;
  label: string;
  disabled: boolean;
  /** Visible text saying why the preset is disabled; null when it is not. */
  reason: string | null;
};

const COMPARE_MODES: readonly CompareMode[] = ["none", "previous"];
const PLATFORMS: readonly AnalyticsPlatform[] = PLATFORM_OPTIONS.map((option) => option.value);
const PREFERRED_DEFAULT_RANGE: RangePreset = "30d";

export const DEFAULT_URL_FILTERS: AnalyticsUrlFilters = {
  range: PREFERRED_DEFAULT_RANGE,
  start: null,
  end: null,
  compare: "previous",
  granularity: "day",
  platform: "all",
};

/** Label and length of each range. Days back from today (UTC) is `days - 1`; custom has no length. */
const RANGE_PRESETS: Record<RangePreset, { label: string; days: number | null }> = {
  "24h": { label: "Last 24 hours", days: 1 },
  today: { label: "Today", days: 1 },
  yesterday: { label: "Yesterday", days: 1 },
  "7d": { label: "7d", days: 7 },
  "30d": { label: "30d", days: 30 },
  "90d": { label: "90d", days: 90 },
  custom: { label: "Custom", days: null },
};

const FULL_PRESETS: readonly RangePreset[] = ["today", "yesterday", "7d", "30d", "90d", "custom"];
const DAY_AND_WEEK: readonly Granularity[] = ["day", "week"];

const view = (
  id: AnalyticsViewId,
  presets: readonly RangePreset[] = FULL_PRESETS,
): AnalyticsViewDefinition => ({ id, presets, granularities: DAY_AND_WEEK, rawWindowCapDays: null });

// Product records reads neither: its cards always print the change against the previous period,
// and its trend tables are daily.
export const PRODUCT_RECORDS_VIEW: AnalyticsViewDefinition = {
  ...view("product-records"),
  ignores: {
    compare: "Product records always compares with the previous period.",
    granularity: "Product records shows days only.",
  },
};

/**
 * The per-screen preset map (AC3), keyed by the screen ids of `ANALYTICS_CHILDREN`
 * (`src/pages/analytics/analyticsRoutes.ts`), which owns the paths.
 */
export const ANALYTICS_VIEWS: readonly AnalyticsViewDefinition[] = [
  view("SCR-1"),
  view("SCR-2"),
  view("SCR-3", ["7d", "30d"]),
  view("SCR-4"),
  view("SCR-5", ["yesterday", "7d", "30d"]),
  view("SCR-6"),
  view("SCR-7", ["24h", "yesterday", "7d", "30d"]),
  view("SCR-8"),
  PRODUCT_RECORDS_VIEW,
];

const isLongerThanCap = (preset: RangePreset, capDays: number | null) => {
  const days = RANGE_PRESETS[preset].days;
  return capDays !== null && days !== null && days > capDays;
};

export const rangePresetOptions = (target: AnalyticsViewDefinition): RangePresetOption[] =>
  target.presets.map((key) => {
    const { label } = RANGE_PRESETS[key];
    const disabled = isLongerThanCap(key, target.rawWindowCapDays);
    return {
      key,
      label,
      disabled,
      reason: disabled ? `${label} is longer than this view's ${target.rawWindowCapDays}-day limit.` : null,
    };
  });

const isOffered = (target: AnalyticsViewDefinition, preset: RangePreset) =>
  target.presets.includes(preset) && !isLongerThanCap(preset, target.rawWindowCapDays);

/** 30d, or under a tighter raw-window cap the longest preset the view still allows. */
export const defaultRangeFor = (target: AnalyticsViewDefinition): RangePreset => {
  if (isOffered(target, PREFERRED_DEFAULT_RANGE)) return PREFERRED_DEFAULT_RANGE;
  const allowed = target.presets.filter(
    (preset) => RANGE_PRESETS[preset].days !== null && isOffered(target, preset),
  );
  return allowed.reduce<RangePreset>(
    (longest, preset) => ((RANGE_PRESETS[preset].days ?? 0) > (RANGE_PRESETS[longest].days ?? 0) ? preset : longest),
    allowed[0] ?? PREFERRED_DEFAULT_RANGE,
  );
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar date written as YYYY-MM-DD (2026-02-30 is not one). */
const isIsoDate = (value: string | null): value is string => {
  if (value === null || !ISO_DATE.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

const includes = <T extends string>(values: readonly T[], value: string): value is T =>
  (values as readonly string[]).includes(value);

const toSearchParams = (search: string | URLSearchParams) =>
  typeof search === "string" ? new URLSearchParams(search) : search;

const isSharedParam = (key: string) => SHARED_FILTER_PARAMS.includes(key);

/** Reads one closed-set param: absent is the default, anything outside the set is a reset. */
const readChoice = <T extends string>(
  params: URLSearchParams,
  param: string,
  allowed: readonly T[],
  fallback: T,
  resets: FilterReset[],
): T => {
  const value = params.get(param);
  if (value === null) return fallback;
  if (includes(allowed, value)) return value;
  resets.push({ param, value, fallback });
  return fallback;
};

export const parseUrlFilters = (
  search: string | URLSearchParams,
  target: AnalyticsViewDefinition,
): ParsedUrlFilters => {
  const params = toSearchParams(search);
  const resets: FilterReset[] = [];
  const defaultRange = defaultRangeFor(target);
  const offered = target.presets.filter((preset) => isOffered(target, preset));

  let range = readChoice(params, "range", offered, defaultRange, resets);
  let start: string | null = null;
  let end: string | null = null;
  if (range === "custom") {
    const rawStart = params.get("start");
    const rawEnd = params.get("end");
    const dateResets: FilterReset[] = [];
    if (!isIsoDate(rawStart)) dateResets.push({ param: "start", value: rawStart, fallback: `range ${defaultRange}` });
    if (!isIsoDate(rawEnd)) dateResets.push({ param: "end", value: rawEnd, fallback: `range ${defaultRange}` });
    if (dateResets.length === 0 && rawStart! > rawEnd!) {
      dateResets.push({ param: "end", value: rawEnd, fallback: `range ${defaultRange}` });
    }
    if (dateResets.length > 0) {
      resets.push(...dateResets);
      range = defaultRange;
    } else {
      start = rawStart;
      end = rawEnd;
    }
  }

  const filters: AnalyticsUrlFilters = {
    range,
    start,
    end,
    compare: readChoice(params, "compare", COMPARE_MODES, DEFAULT_URL_FILTERS.compare, resets),
    granularity: readChoice(params, "granularity", target.granularities, DEFAULT_URL_FILTERS.granularity, resets),
    platform: readChoice(params, "platform", PLATFORMS, DEFAULT_URL_FILTERS.platform, resets),
  };
  const pageParams = [...params.entries()].filter(([key]) => !isSharedParam(key));
  return { filters, pageParams, resets };
};

/** The address query for these filters: shared params first, defaults left out, then page params. */
export const serializeUrlFilters = (
  filters: AnalyticsUrlFilters,
  target: AnalyticsViewDefinition,
  pageParams: Array<[string, string]> = [],
): URLSearchParams => {
  const params = new URLSearchParams();
  if (filters.range !== defaultRangeFor(target)) params.set("range", filters.range);
  if (filters.range === "custom" && filters.start && filters.end) {
    params.set("start", filters.start);
    params.set("end", filters.end);
  }
  if (filters.compare !== DEFAULT_URL_FILTERS.compare) params.set("compare", filters.compare);
  if (filters.granularity !== DEFAULT_URL_FILTERS.granularity) params.set("granularity", filters.granularity);
  if (filters.platform !== DEFAULT_URL_FILTERS.platform) params.set("platform", filters.platform);
  for (const [key, value] of pageParams) params.append(key, value);
  return params;
};

const utcDay = (now: Date, daysBack: number) =>
  new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - daysBack))
    .toISOString()
    .slice(0, 10);

/** The UTC calendar days a range covers on `now`; `end` is inclusive. */
export const resolveDateRange = (filters: AnalyticsUrlFilters, now: Date): DateRange => {
  const { range } = filters;
  if (range === "custom" && filters.start && filters.end) {
    return { preset: range, start: filters.start, end: filters.end };
  }
  const today = utcDay(now, 0);
  if (range === "yesterday") {
    const yesterday = utcDay(now, 1);
    return { preset: range, start: yesterday, end: yesterday };
  }
  if (range === "24h") return { preset: range, start: utcDay(now, 1), end: today };
  const days = RANGE_PRESETS[range].days ?? RANGE_PRESETS[PREFERRED_DEFAULT_RANGE].days!;
  return { preset: range, start: utcDay(now, days - 1), end: today };
};

const describeReset = ({ param, value, fallback }: FilterReset) =>
  `${param} ${value === null ? "(missing)" : `"${value}"`} is now ${fallback}`;

/** One message for every value reset on one load (STATE-28, proposed copy). */
export const filterResetMessage = (resets: FilterReset[]): string =>
  resets.length === 1
    ? `Reset an invalid filter in this link: ${describeReset(resets[0])}.`
    : `Reset ${resets.length} invalid filters in this link: ${resets.map(describeReset).join("; ")}.`;

/**
 * The view an analytics path belongs to, through its sidebar child; null outside a child
 * (`/analytics` while it redirects). Product records has no screen id (ANL-E04-S03).
 */
export const analyticsViewForPath = (pathname: string): AnalyticsViewDefinition | null => {
  const child = analyticsChildForPath(pathname);
  if (!child) return null;
  const id = child.screenId ?? PRODUCT_RECORDS_VIEW.id;
  return ANALYTICS_VIEWS.find((candidate) => candidate.id === id) ?? null;
};

const isAnalyticsPath = (path: string) => path === ANALYTICS_PATH || path.startsWith(`${ANALYTICS_PATH}/`);

/**
 * A link from one analytics view to another keeps the shared filters and drops the page params
 * (AC5), the same way the analytics redirects do. Links into or out of analytics are left alone.
 */
export const analyticsLinkWithSharedParams = (
  url: string,
  from: { pathname: string; search: string },
): string => {
  if (!isAnalyticsPath(url) || !isAnalyticsPath(from.pathname)) return url;
  return `${url}${keepSharedFilterParams(from.search)}`;
};
